import {useEffect, useMemo, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Eye, KeyRound, Pause, Pencil, Play, Plus, ShieldCheck, Ticket, UserCog} from "lucide-react";
import {mapApiError} from "../../shared/api/errors";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {AdminTable} from "../../shared/components/AdminTable";
import {ConfirmationDialog} from "../../shared/components/ConfirmationDialog";
import {DataState} from "../../shared/components/DataState";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {PageSizeSelect} from "../../shared/components/PageSizeSelect";
import {PageToolbar, ToolbarButton, ToolbarInput} from "../../shared/components/PageToolbar";
import {ServerPagination} from "../../shared/components/ServerPagination";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {formatNumber} from "../../shared/utils/format";
import {
    approveDeployerLicenseRequest,
    createDeployer,
    editDeployer,
    grantDeployerLicenses,
    loadDeployer,
    loadDeployerLicenseRequests,
    loadDeployers,
    rejectDeployerLicenseRequest,
    rotateDeployerCredential,
    toggleDeployerActive,
} from "./api";
import {
    deployerQueryKeys,
    normalizeDeployerPage,
    parsePageFromPaginationUrl,
} from "./contracts";
import {CredentialRotationDialog} from "./components/CredentialRotationDialog";
import {DialogShell} from "./components/DialogShell";
import {DeployerFormDialog} from "./components/DeployerFormDialog";
import {DeployerProfileDialog} from "./components/DeployerProfileDialog";
import {OneTimeCredentialDialog} from "./components/OneTimeCredentialDialog";
import {useDeployerCountryScope} from "./useDeployerCountryScope";
import "./deployers.css";
import {useTranslation, i18n} from "../../shared/i18n";


const PLATFORM_ROLES = new Set([ADMIN_ROLES.SUPERADMIN, ADMIN_ROLES.ADMINISTRATOR]);

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];

const deployerLabel = (deployer) => deployer?.name || deployer?.username || `#${deployer?.id}`;

export const DeployersPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const queryClient = useQueryClient();
    const {countryScope, countryId, activeCountryScopeRef} = useDeployerCountryScope(capabilities);
    const [draftSearch, setDraftSearch] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [selectedId, setSelectedId] = useState(null);
    const [dialog, setDialog] = useState({type: null, deployer: null});
    const [pending, setPending] = useState(false);
    const [dialogError, setDialogError] = useState(null);
    const [result, setResult] = useState(null);
    const [oneTimeCredential, setOneTimeCredential] = useState(null);
    const [staffRole, setStaffRole] = useState(ADMIN_ROLES.DEPLOYER);
    const [grantQty, setGrantQty] = useState("1");
    const commandEpochRef = useRef(0);
    const agentsTab = staffRole === ADMIN_ROLES.AGENT;

    useEffect(() => {
        commandEpochRef.current += 1;
        setPage(1);
        setPageSize(DEFAULT_PAGE_SIZE);
        setDraftSearch("");
        setSearch("");
        setSelectedId(null);
        setDialog({type: null, deployer: null});
        setDialogError(null);
        setResult(null);
        setOneTimeCredential(null);
        setPending(false);
    }, [countryScope]);

    const deployersQuery = useQuery({
        queryKey: [...deployerQueryKeys.list({countryId: countryScope, page, search, pageSize}), staffRole],
        queryFn: async () => normalizeDeployerPage(await loadDeployers({
            countryId: countryScope,
            page,
            search,
            pageSize,
            ...(agentsTab ? {staffRole} : {}),
        })),
        enabled: countryScope !== null,
        retry: false,
    });
    const detailQuery = useQuery({
        queryKey: deployerQueryKeys.detail({deployerId: selectedId, countryId: countryScope}),
        queryFn: () => loadDeployer({deployerId: selectedId, countryId: countryScope}),
        enabled: selectedId !== null,
        retry: false,
    });
    const canReviewLicenses = !agentsTab && (
        (PLATFORM_ROLES.has(capabilities.user?.role) && capabilities.hasAction("user.manage"))
        || (
            capabilities.user?.role === ADMIN_ROLES.IMPLEMENTATION_HEAD
            && capabilities.hasAction("deployers.manage")
        )
    );
    const licenseRequestsQuery = useQuery({
        queryKey: ["deployer-license-requests", "pending"],
        queryFn: () => loadDeployerLicenseRequests({status: "pending"}),
        enabled: canReviewLicenses,
        retry: false,
    });
    const licenseRequestMutation = useMutation({
        mutationFn: ({id, action}) => (
            action === "approve" ? approveDeployerLicenseRequest(id) : rejectDeployerLicenseRequest(id)
        ),
        onSuccess: async (_data, variables) => {
            setResult({
                kind: "success",
                message: variables.action === "approve" ? t("sections.deployers.p73") : t("sections.deployers.p74"),
            });
            await queryClient.invalidateQueries({queryKey: ["deployer-license-requests"]});
        },
        onError: (error) => {
            setResult({kind: "error", message: mapApiError(error)});
        },
    });

    const rows = deployersQuery.data?.results || [];
    const activeOnPage = rows.filter((row) => row.is_active).length;
    const canManage = countryId !== null && (
        (PLATFORM_ROLES.has(capabilities.user?.role) && capabilities.hasAction("user.manage"))
        || (
            capabilities.user?.role === ADMIN_ROLES.IMPLEMENTATION_HEAD
            && capabilities.hasAction("deployers.manage")
        )
    );
    const actorCountryId = String(capabilities.country?.id ?? "");
    const canCreate = canManage && (
        capabilities.user?.role === ADMIN_ROLES.SUPERADMIN
        || countryId === actorCountryId
    );

    const invalidateDeployerData = async (deployerId = null) => {
        await queryClient.invalidateQueries({queryKey: deployerQueryKeys.all});
        if (deployerId !== null) {
            await queryClient.invalidateQueries({
                queryKey: deployerQueryKeys.detail({deployerId, countryId: countryScope}),
            });
        }
    };

    const closeDialog = () => {
        if (pending) return;
        setDialog({type: null, deployer: null});
        setDialogError(null);
    };

    const runCommand = async ({scope, command, successMessage, afterSuccess}) => {
        const commandEpoch = commandEpochRef.current + 1;
        commandEpochRef.current = commandEpoch;
        setPending(true);
        setDialogError(null);
        setResult(null);
        try {
            await command();
            if (activeCountryScopeRef.current !== scope || commandEpochRef.current !== commandEpoch) return;
            setDialog({type: null, deployer: null});
            setResult({kind: "success", message: successMessage});
            if (afterSuccess) afterSuccess();
            await invalidateDeployerData(dialog.deployer?.id || null);
        } catch (error) {
            if (activeCountryScopeRef.current !== scope || commandEpochRef.current !== commandEpoch) return;
            const mapped = mapApiError(error);
            setDialogError(mapped);
            setResult({kind: "error", message: mapped.message});
        } finally {
            if (activeCountryScopeRef.current === scope && commandEpochRef.current === commandEpoch) setPending(false);
        }
    };

    const submitProfile = (values) => {
        const scope = countryScope;
        if (dialog.type === "create") {
            void runCommand({
                scope,
                command: () => createDeployer({
                    countryId,
                    ...values,
                    ...(agentsTab ? {staffRole: ADMIN_ROLES.AGENT} : {}),
                }),
                successMessage: t("sections.deployers.p1"),
                afterSuccess: () => setOneTimeCredential({
                    title: t("sections.deployers.p2"),
                    username: values.username.trim(),
                    password: values.password,
                }),
            });
            return;
        }
        const target = dialog.deployer;
        void runCommand({
            scope,
            command: () => editDeployer({deployerId: target.id, countryId, ...values}),
            successMessage: t("sections.deployers.p3"),
        });
    };

    const confirmToggle = () => {
        const target = dialog.deployer;
        const activating = !target.is_active;
        const scope = countryScope;
        void runCommand({
            scope,
            command: () => toggleDeployerActive({deployerId: target.id, countryId}),
            successMessage: activating ? t("sections.deployers.p4") : t("sections.deployers.p5"),
        });
    };

    const submitRotation = (password) => {
        const target = dialog.deployer;
        const scope = countryScope;
        void runCommand({
            scope,
            command: () => rotateDeployerCredential({deployerId: target.id, countryId, password}),
            successMessage: t("sections.deployers.p6"),
            afterSuccess: () => setOneTimeCredential({
                title: t("sections.deployers.p7"),
                username: target.username,
                password,
            }),
        });
    };

    const submitGrant = () => {
        const target = dialog.deployer;
        const quantity = Math.min(99, Math.max(1, Number.parseInt(grantQty, 10) || 1));
        setGrantQty(String(quantity));
        const scope = countryScope;
        void runCommand({
            scope,
            command: () => grantDeployerLicenses({deployerId: target.id, quantity}),
            successMessage: t("sections.deployers.granted"),
        });
    };

    const openProfile = (row) => {
        setSelectedId(row.id);
        setDialogError(null);
    };

    const openCommand = (type, target = null) => {
        setDialog({type, deployer: target});
        setDialogError(null);
        setResult(null);
    };

    const columns = useMemo(() => [
        {
            key: "name",
            label: t("sections.deployers.p8"),
            render: (row) => (
                <div>
                    <strong>{deployerLabel(row)}</strong>
                    <div className="admin-muted">ID {row.id}</div>
                </div>
            ),
        },
        {key: "phone_number", label: t("sections.deployers.p9"), render: (row) => row.phone_number || "—"},
        {key: "username", label: t("sections.deployers.p10"), render: (row) => row.username || "—"},
        {key: "email", label: "Email", render: (row) => row.email || "—"},
        ...(agentsTab ? [] : [
            {
                key: "commission_percent",
                label: t("sections.deployers.p75"),
                render: (row) => `${formatNumber(row.commission_percent ?? 0)}%`,
            },
            {
                key: "licenses_remaining",
                label: t("sections.deployers.licensesLeft"),
                render: (row) => (
                    row.licenses_remaining === null || row.licenses_remaining === undefined
                        ? t("sections.deployers.licensesUnlimited")
                        : formatNumber(row.licenses_remaining)
                ),
            },
        ]),
        {
            key: "status",
            label: t("sections.deployers.p11"),
            render: (row) => (
                <StatusBadge tone={row.is_active ? "success" : "danger"}>
                    {row.is_active ? i18n.t("sections.deployers.p12") : i18n.t("sections.deployers.p13")}
                </StatusBadge>
            ),
        },
        {
            key: "actions",
            label: t("sections.deployers.p14"),
            render: (row) => (
                <div className="admin-inline-actions">
                    <button className="admin-icon-button" type="button" title={i18n.t("sections.deployers.p15")} aria-label={i18n.t("sections.deployers.p16", {v0: deployerLabel(row)})} onClick={() => openProfile(row)}>
                        <Eye size={16} />
                    </button>
                    {canManage ? (
                        <>
                            <button className="admin-icon-button" type="button" title={i18n.t("sections.deployers.p17")} aria-label={i18n.t("sections.deployers.p18", {v0: deployerLabel(row)})} onClick={() => openCommand("edit", row)}>
                                <Pencil size={16} />
                            </button>
                            <button className="admin-icon-button" type="button" title={i18n.t("sections.deployers.p19")} aria-label={i18n.t("sections.deployers.p20", {v0: deployerLabel(row)})} onClick={() => openCommand("rotate", row)}>
                                <KeyRound size={16} />
                            </button>
                            <button className={`admin-icon-button${row.is_active ? " is-danger" : ""}`} type="button" title={row.is_active ? i18n.t("sections.deployers.p21") : i18n.t("sections.deployers.p22")} aria-label={`${row.is_active ? i18n.t("sections.deployers.p21") : i18n.t("sections.deployers.p22")} ${deployerLabel(row)}`} onClick={() => openCommand("toggle", row)}>
                                {row.is_active ? <Pause size={16} /> : <Play size={16} />}
                            </button>
                            {!agentsTab ? (
                                <button className="admin-icon-button" type="button" title={i18n.t("sections.deployers.grantLicenses")} aria-label={`${i18n.t("sections.deployers.grantLicenses")} ${deployerLabel(row)}`} onClick={() => { setGrantQty("1"); openCommand("grant", row); }}>
                                    <Ticket size={16} />
                                </button>
                            ) : null}
                        </>
                    ) : null}
                </div>
            ),
        },
    ], [agentsTab, canManage, t]);

    return (
        <div className="admin-page deployers-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{t("sections.deployers.p23")}</h1>
                    <p className="admin-page-copy">{t("sections.deployers.p24")}</p>
                </div>
                {canCreate ? (
                    <button type="button" className="admin-button admin-button-primary" onClick={() => openCommand("create")}>
                        <Plus size={16} /> {agentsTab ? t("sections.deployers.tabCreateAgent") : t("sections.deployers.p25")}
                    </button>
                ) : (
                    <div className="admin-pill"><UserCog size={14} /> {formatNumber(deployersQuery.data?.count)}</div>
                )}
            </header>

            <section className="admin-grid cols-3">
                <article className="admin-card">
                    <p className="admin-card-title">{t("sections.deployers.p26")}</p>
                    <p className="admin-card-value">{formatNumber(deployersQuery.data?.count)}</p>
                    <p className="admin-card-note">{t("sections.deployers.p27")}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title"><ShieldCheck size={14} /> {t("sections.deployers.p28")}</p>
                    <p className="admin-card-value">{formatNumber(activeOnPage)}</p>
                    <p className="admin-card-note">{t("sections.deployers.p29")} {formatNumber(rows.length)} {t("sections.deployers.p30")}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title">{t("sections.deployers.p31")}</p>
                    <p className="admin-card-value">{countryScope === "0" ? t("sections.deployers.p32") : countryScope || "—"}</p>
                    <p className="admin-card-note">{t("sections.deployers.p33")}</p>
                </article>
            </section>

            <MutationResultBanner result={result} />
            {canManage ? (
                <div className="admin-inline-actions" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={!agentsTab}
                        className={`admin-button ${agentsTab ? "admin-button-secondary" : "admin-button-primary"}`}
                        onClick={() => {
                            setStaffRole(ADMIN_ROLES.DEPLOYER);
                            setPage(1);
                            setSelectedId(null);
                        }}
                    >
                        {t("sections.deployers.tabDeployers")}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={agentsTab}
                        className={`admin-button ${agentsTab ? "admin-button-primary" : "admin-button-secondary"}`}
                        onClick={() => {
                            setStaffRole(ADMIN_ROLES.AGENT);
                            setPage(1);
                            setSelectedId(null);
                        }}
                    >
                        {t("sections.deployers.tabAgents")}
                    </button>
                </div>
            ) : null}
            {!agentsTab && (licenseRequestsQuery.data?.results || []).length ? (
                <section className="admin-card">
                    <p className="admin-card-title">{t("sections.deployers.p68")}</p>
                    <p className="admin-card-note">{t("sections.deployers.p69")}</p>
                    <AdminTable
                        columns={[
                            {
                                key: "deployer_name",
                                label: t("sections.deployers.p8"),
                                render: (row) => row.deployer_name || `#${row.deployer_id}`,
                            },
                            {key: "quantity", label: t("sections.deployers.p72")},
                            {
                                key: "actions",
                                label: t("sections.deployers.p14"),
                                render: (row) => canManage ? (
                                    <div className="admin-inline-actions">
                                        <button
                                            className="admin-button admin-button-primary"
                                            type="button"
                                            disabled={licenseRequestMutation.isPending}
                                            onClick={() => licenseRequestMutation.mutate({id: row.id, action: "approve"})}
                                        >
                                            {t("sections.deployers.p70")}
                                        </button>
                                        <button
                                            className="admin-button admin-button-secondary"
                                            type="button"
                                            disabled={licenseRequestMutation.isPending}
                                            onClick={() => licenseRequestMutation.mutate({id: row.id, action: "reject"})}
                                        >
                                            {t("sections.deployers.p71")}
                                        </button>
                                    </div>
                                ) : null,
                            },
                        ]}
                        rows={licenseRequestsQuery.data.results}
                    />
                </section>
            ) : null}
            {!countryId ? (
                <DataState title={t("sections.deployers.p34")} copy={t("sections.deployers.p35")} />
            ) : null}

            <PageToolbar>
                <ToolbarInput
                    label={t("sections.deployers.p36")}
                    type="search"
                    placeholder={t("sections.deployers.p37")}
                    value={draftSearch}
                    onChange={(event) => setDraftSearch(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            setPage(1);
                            setSearch(draftSearch.trim());
                        }
                    }}
                />
                <ToolbarButton onClick={() => { setPage(1); setSearch(draftSearch.trim()); }}>{t("sections.deployers.p38")}</ToolbarButton>
                <ToolbarButton variant="secondary" onClick={() => { setDraftSearch(""); setSearch(""); setPage(1); }}>{t("sections.deployers.p39")}</ToolbarButton>
                <PageSizeSelect
                    value={pageSize}
                    options={PAGE_SIZE_OPTIONS}
                    onChange={(nextPageSize) => { setPage(1); setPageSize(nextPageSize); }}
                />
            </PageToolbar>

            {deployersQuery.isError ? (
                <div className="admin-card admin-state" role="alert">
                    <strong>{t("sections.deployers.p40")}</strong>
                    <p>{mapApiError(deployersQuery.error).message}</p>
                </div>
            ) : (
                <>
                    <AdminTable columns={columns} rows={rows} loading={deployersQuery.isLoading} emptyText={t("sections.deployers.p41")} />
                    {!deployersQuery.isLoading ? (
                        <ServerPagination
                            count={deployersQuery.data?.count}
                            page={page}
                            pageSize={pageSize}
                            previous={deployersQuery.data?.previous}
                            next={deployersQuery.data?.next}
                            pending={deployersQuery.isFetching}
                            onPageChange={(link) => setPage((current) => parsePageFromPaginationUrl(link, current))}
                        />
                    ) : null}
                </>
            )}

            <DeployerProfileDialog
                open={selectedId !== null}
                deployer={detailQuery.data}
                loading={detailQuery.isLoading}
                error={detailQuery.isError ? mapApiError(detailQuery.error) : null}
                canManage={canManage}
                onClose={() => setSelectedId(null)}
                onEdit={(target) => { setSelectedId(null); openCommand("edit", target); }}
                onRotate={(target) => { setSelectedId(null); openCommand("rotate", target); }}
                onToggle={(target) => { setSelectedId(null); openCommand("toggle", target); }}
            />
            <DeployerFormDialog
                open={dialog.type === "create" || dialog.type === "edit"}
                mode={dialog.type === "edit" ? "edit" : "create"}
                deployer={dialog.deployer}
                pending={pending}
                error={dialogError}
                hideCommission={agentsTab}
                createTitle={agentsTab ? t("sections.deployers.tabCreateAgent") : undefined}
                onCancel={closeDialog}
                onSubmit={submitProfile}
            />
            <DialogShell
                open={dialog.type === "grant"}
                title={t("sections.deployers.grantLicenses")}
                entity={deployerLabel(dialog.deployer)}
                pending={pending}
                onClose={closeDialog}
            >
                <form className="deployer-form" onSubmit={(event) => { event.preventDefault(); submitGrant(); }}>
                    <label className="admin-field">
                        <span>{t("sections.deployers.grantQuantity")}</span>
                        <input
                            type="number"
                            min="1"
                            max="99"
                            inputMode="numeric"
                            required
                            disabled={pending}
                            value={grantQty}
                            onChange={(event) => setGrantQty(event.target.value)}
                        />
                    </label>
                    {dialogError?.message ? <div className="deployer-dialog-error" role="alert">{dialogError.message}</div> : null}
                    <footer className="deployer-dialog-footer">
                        <button type="button" className="admin-button admin-button-secondary" disabled={pending} onClick={closeDialog}>{t("sections.deployers.p50")}</button>
                        <button type="submit" className="admin-button admin-button-primary" disabled={pending}>{t("sections.deployers.grantLicenses")}</button>
                    </footer>
                </form>
            </DialogShell>
            <CredentialRotationDialog
                open={dialog.type === "rotate"}
                deployer={dialog.deployer}
                pending={pending}
                error={dialogError}
                onCancel={closeDialog}
                onConfirm={submitRotation}
            />
            <ConfirmationDialog
                open={dialog.type === "toggle"}
                title={dialog.deployer?.is_active ? t("sections.deployers.p42") : t("sections.deployers.p43")}
                entity={deployerLabel(dialog.deployer)}
                description={dialog.deployer?.is_active
                    ? t("sections.deployers.p44")
                    : t("sections.deployers.p45")}
                confirmLabel={dialog.deployer?.is_active ? t("sections.deployers.p21") : t("sections.deployers.p22")}
                tone={dialog.deployer?.is_active ? "destructive" : "primary"}
                pending={pending}
                error={dialogError}
                onCancel={closeDialog}
                onConfirm={confirmToggle}
            />
            <OneTimeCredentialDialog
                credential={oneTimeCredential}
                onClose={() => setOneTimeCredential(null)}
            />
        </div>
    );
};
